import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import SplitText from 'gsap/dist/SplitText'
import { merge } from 'lodash'

import Grid, { GridProps } from '@/components/base/Grid'
import Typography from '@/components/base/Typography'
import { SectionHandler } from '@/components/sections/HeroSection'
import { useTimeline } from '@/contexts/timeline'
import { useLoader } from '@/store/loader'

// import { isLgScreen } from '@/utils/screen'
import { cn } from '@/lib/utils'

export type LandingProps = GridProps & {
  overline: string
  title: string
  variant?: 'large' | 'small'
}

const Landing = forwardRef<SectionHandler, LandingProps>(
  ({ overline, title, variant, ...props }, ref) => {
    const root = useRef<HTMLDivElement>(null)
    const component = useMemo(() => (variant === 'large' ? 'h1' : 'h3'), [variant])
    const titleVariant = useMemo(() => (variant === 'large' ? 'h1' : 'h5'), [variant])
    const overlineVariant = useMemo(() => (variant === 'large' ? 'h2' : 'h6'), [variant])

    const { context, contextSafe } = useGSAP(() => {}, { scope: root })

    const splitOverline = useRef<SplitText>(null!)
    const splitTitle = useRef<SplitText>(null!)

    const timeline = useTimeline()

    const createTimeline = contextSafe(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: { ease: 'none' }
      })

      return timeline
    })

    const createEnterTimeline = contextSafe((vars?: GSAPTimelineVars) => {
      const overline = context.selector?.('[data-motion-overline]')
      const title = context.selector?.('[data-motion-title]')
      // const splitType = !isLgScreen() ? 'lines,chars' : 'words,chars'
      const splitType = 'lines,chars'
      splitOverline.current = SplitText.create(overline, {
        type: splitType,
        linesClass: 'line'
      })
      splitTitle.current = SplitText.create(title, {
        type: splitType,
        linesClass: 'line'
      })

      const timeline = gsap.timeline(
        merge(
          { paused: true, defaults: { duration: 1, ease: 'power3.inOut', stagger: 0.01 } },
          vars
        )
      )

      timeline
        .from(splitTitle.current.chars, { yPercent: 110, opacity: 0 })
        .from(splitOverline.current.chars, { yPercent: 110, opacity: 0 }, 0)

      return timeline
    })

    const createLeaveTimeline = contextSafe(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: { duration: 1, ease: 'power3.inOut', immediateRender: false, stagger: 0.01 }
      })

      timeline
        .to(splitTitle.current.chars, { yPercent: 110, opacity: 0 })
        .to(splitOverline.current.chars, { yPercent: 110, opacity: 0 }, 0)

      return timeline
    })

    const resetTimeline = contextSafe(() => {
      splitOverline.current && splitOverline.current.revert()
      splitTitle.current && splitTitle.current.revert()
    })

    const reveal = contextSafe(() => {
      if (variant === 'small') return gsap.timeline()
      console.log('landing.reveal')
      return createEnterTimeline({ paused: false, defaults: { duration: 1.5, stagger: 0.03 } })
    })

    useIsomorphicLayoutEffect(() => {
      const unsubscribe = useLoader.subscribe(
        (state) => state.compiled,
        (compiled) => {
          if (compiled) {
            unsubscribe()
            timeline.add(reveal(), 2)
          }
        }
      )

      return () => {
        unsubscribe()
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        createTimeline,
        createEnterTimeline,
        createLeaveTimeline,
        resetTimeline
      }),
      [createTimeline, createEnterTimeline, createLeaveTimeline, resetTimeline]
    )

    return (
      <Grid
        ref={root}
        className={cn([
          'gap-y-spacing-sm',
          {
            'md:gap-y-spacing-lg': variant === 'large',
            'lg:gap-y-spacing-xs': variant === 'small'
          }
        ])}
        {...props}
      >
        <div className="col-span-4 md:col-span-6 lg:col-span-9 xl:col-span-8 2xl:col-span-7">
          <div className="flex">
            <Typography
              variant={overlineVariant}
              component={component}
              className={cn([
                'pr-[0.1em] text-text-accent-dark lg:overflow-hidden',
                '[&_.line]:overflow-hidden [&_.line]:pr-[0.1em] lg:[&_.line]:pr-0'
              ])}
              data-motion-overline
            >
              {overline}
            </Typography>
          </div>
        </div>
        <div className="col-span-4 md:col-span-6 lg:col-span-9 xl:col-span-8 2xl:col-span-7">
          <div className="flex">
            <Typography
              variant={titleVariant}
              component={component}
              className={cn([
                'leading-[100%] text-text-primary-dark lg:overflow-hidden lg:pr-[0.1em]',
                '[&_.line]:-mt-[0.1em] [&_.line]:overflow-hidden [&_.line]:py-[0.1em] [&_.line]:pr-[0.1em]',
                // '[&_small]:-translate-y-[2rem]',
                {
                  '[&_b]:!font-normal': variant === 'small'
                }
              ])}
              data-motion-title
            >
              {title}
            </Typography>
          </div>
        </div>
      </Grid>
    )
  }
)

export default Landing
